import { useState } from "react";
import { motion } from "framer-motion";
import {
  Coins,
  Wrench,
  Users,
  Clock,
  ArrowRight,
  Check,
  Loader2,
  Mail,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import heroCafe from "@/assets/pod-partner/hero-cafe.jpg";
import venueCafe from "@/assets/pod-partner/venue-cafe.jpg";
import venueHotel from "@/assets/pod-partner/venue-hotel.jpg";
import venueFitness from "@/assets/pod-partner/venue-fitness.jpg";
import venueCowork from "@/assets/pod-partner/venue-cowork.jpg";

const benefits = [
  {
    icon: Coins,
    title: "passive revenue",
    body: "earn a share of every booking - up to £2,600+ per pod each year, with zero work on your side.",
  },
  {
    icon: Wrench,
    title: "zero setup cost",
    body: "we deliver, install, maintain and support the pod. you don't pay a penny upfront.",
  },
  {
    icon: Users,
    title: "new foot traffic",
    body: "qwip's app and live map of spots brings new customers through your door looking for a quiet place to work.",
  },
  {
    icon: Clock,
    title: "always-on amenity",
    body: "a 24/7 private workspace that increases dwell time, average spend and loyalty - without lifting a finger.",
  },
];

const venues = [
  { img: venueCafe, label: "cafés & coffee shops" },
  { img: venueHotel, label: "hotels & lobbies" },
  { img: venueFitness, label: "Fitness Clubs" },
  { img: venueCowork, label: "co-working & Transit Hubs" },
];

const steps = [
  {
    n: "01",
    title: "apply",
    body: "tell us about your space in under 2 minutes using the form below.",
  },
  {
    n: "02",
    title: "site check",
    body: "we confirm the pod fits - footprint, power, wifi - and agree a spot.",
  },
  {
    n: "03",
    title: "install",
    body: "we handle delivery and setup end-to-end. no cost, no disruption.",
  },
  {
    n: "04",
    title: "earn",
    body: "your revenue share kicks in from day one of the first booking.",
  },
];

const youProvide = [
  "~1.2m² of floor space, ideally near a wall",
  "a standard power socket within reach",
  "wifi access (or we'll add a hotspot)",
  "a green light from your landlord (if needed)",
];

const weProvide = [
  "the pod itself - soundproof, climate-controlled, lit",
  "delivery, install and ongoing maintenance",
  "24/7 customer support for every booking",
  "marketing - your venue listed on the qwip app & map",
  "monthly revenue payouts, no admin",
];

const faqs = [
  {
    q: "how much space do I actually need?",
    a: "roughly 1.2m² of floor space - about the size of a small armchair. we'll do a free site check before anything is committed.",
  },
  {
    q: "who handles maintenance and cleaning?",
    a: "we do. our team handles all servicing remotely and on-site. cleaning is built into our standard care, so it's never on your staff.",
  },
  {
    q: "what if something breaks or a customer has an issue?",
    a: "every pod has 24/7 support built in - customers contact us directly via the app and WhatsApp. you'll never be the first line of support.",
  },
  {
    q: "can the pod be moved if we rearrange the space?",
    a: "yes. pods are modular and can be repositioned. just give us a heads up and we'll coordinate it.",
  },
  {
    q: "what are the contract terms?",
    a: "flexible. we sign a simple revenue-share agreement - no long lock-ins, no fixed fees. you can pause or end the arrangement with reasonable notice.",
  },
  {
    q: "how and when do I get paid?",
    a: "monthly, direct to your account. you'll see a clean breakdown of bookings and your share each cycle.",
  },
];

const venueOptions = [
  "Café / coffee shop",
  "Hotel",
  "Fitness Club",
  "Co-working space",
  "Transit Hub",
  "Other",
];

const PodPartner = () => {
  const [form, setForm] = useState({
    business: "",
    name: "",
    email: "",
    phone: "",
    venueType: venueOptions[0],
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const scrollToForm = () => {
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.business.trim() || !form.name.trim() || !form.email.trim() || !form.city.trim()) {
      toast.error("please fill in business, name, email and city");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const composed = `New pod-partner application

Business: ${form.business}
Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone || "-"}
Venue type: ${form.venueType}
City: ${form.city}

Message:
${form.message || "(none)"}`;

      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to: "hello@qwip.co.uk",
          subject: `pod-partner application - ${form.business} (${form.venueType})`,
          type: "support",
          name: form.name,
          fromEmail: form.email,
          message: composed,
        },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Failed to send");
      }

      setSent(true);
      toast.success("application sent - we'll be in touch within 1-2 days");
      setForm({
        business: "",
        name: "",
        email: "",
        phone: "",
        venueType: venueOptions[0],
        city: "",
        message: "",
      });
    } catch (err: any) {
      console.error("Pod partner form error:", err);
      toast.error("couldn't send your application. please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-[1200px] mx-auto grid laptop:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-cormorant italic text-2xl text-primary/70 mb-4">
              host a pod
            </p>
            <h1 className="font-funnel font-semibold text-primary text-[40px] tablet:text-[52px] laptop:text-[64px] leading-[1.02] tracking-[-0.02em] mb-6">
              earn passive revenue
              <br />
              from your unused
              <br />
              corner space.
            </h1>
            <p className="text-foreground/70 text-lg mb-8 max-w-[480px]">
              partner with qwip to host a private soundproof workpod - a
              high-margin amenity your customers already want. zero setup cost.
              monthly revenue share.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                className="rounded-full"
                onClick={scrollToForm}
              >
                apply to host <ArrowRight className="w-4 h-4" />
              </Button>
              <a
                href="https://wa.me/447347474059"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" variant="outline" className="rounded-full">
                  <MessageCircle className="w-4 h-4" />
                  chat on WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <img
              src={heroCafe}
              alt="qwip pod installed in a stylish café"
              width={1600}
              height={1088}
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 bg-card/40">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-[640px] mb-14">
            <h2 className="font-funnel font-semibold text-primary text-4xl tablet:text-5xl leading-[1.05] tracking-[-0.02em] mb-4">
              why host a qwip pod?
            </h2>
            <p className="text-foreground/70 text-lg">
              it's the simplest way to add a new revenue stream and a premium
              amenity - without taking on cost, risk or extra work.
            </p>
          </div>
          <div className="grid tablet:grid-cols-2 laptop:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="bg-background border border-border rounded-2xl p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5" />
                </div>
                <h3 className="font-funnel font-semibold text-primary text-xl mb-2">
                  {b.title}
                </h3>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  {b.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-24 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-[640px] mb-14">
            <h2 className="font-funnel font-semibold text-primary text-4xl tablet:text-5xl leading-[1.05] tracking-[-0.02em] mb-4">
              built for venues like yours.
            </h2>
            <p className="text-foreground/70 text-lg">
              if your space sees people who'd love somewhere quiet to take a
              call or focus for an hour, qwip belongs there.
            </p>
          </div>
          <div className="grid tablet:grid-cols-2 laptop:grid-cols-4 gap-5">
            {venues.map((v, i) => (
              <motion.div
                key={v.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="group rounded-2xl overflow-hidden bg-card border border-border"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={v.img}
                    alt={v.label}
                    width={1024}
                    height={1024}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <p className="font-funnel text-primary text-lg">{v.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-card/40">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-funnel font-semibold text-primary text-4xl tablet:text-5xl leading-[1.05] tracking-[-0.02em] mb-14 max-w-[640px]">
            how it works.
          </h2>
          <div className="grid tablet:grid-cols-2 laptop:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="bg-background border border-border rounded-2xl p-6"
              >
                <div className="font-cormorant italic text-primary/40 text-4xl mb-3">
                  {s.n}
                </div>
                <h3 className="font-funnel font-semibold text-primary text-xl mb-2">
                  {s.title}
                </h3>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get / what we provide */}
      <section className="py-24 px-6">
        <div className="max-w-[1200px] mx-auto grid laptop:grid-cols-2 gap-6">
          <div className="bg-card/60 border border-border rounded-2xl p-8">
            <p className="font-cormorant italic text-primary/60 text-xl mb-2">
              from you
            </p>
            <h3 className="font-funnel font-semibold text-primary text-2xl mb-6">
              what you provide
            </h3>
            <ul className="space-y-3">
              {youProvide.map((item) => (
                <li key={item} className="flex gap-3 text-foreground/80">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-primary text-primary-foreground rounded-2xl p-8">
            <p className="font-cormorant italic text-primary-foreground/70 text-xl mb-2">
              from us
            </p>
            <h3 className="font-funnel font-semibold text-2xl mb-6">
              what qwip provides
            </h3>
            <ul className="space-y-3">
              {weProvide.map((item) => (
                <li key={item} className="flex gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Revenue snapshot */}
      <section className="py-24 px-6 bg-card/40">
        <div className="max-w-[900px] mx-auto text-center">
          <p className="font-cormorant italic text-primary/60 text-2xl mb-3">
            estimated host earnings
          </p>
          <p className="font-funnel font-semibold text-primary text-[72px] tablet:text-[120px] leading-none tracking-[-0.04em] mb-4">
            £2,600+
          </p>
          <p className="text-foreground/70 text-lg max-w-[560px] mx-auto">
            per pod, per year. revenue share is paid monthly. exact figures
            depend on footfall and hours, but most partner venues see this
            comfortably within their first 12 months.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-[840px] mx-auto">
          <h2 className="font-funnel font-semibold text-primary text-4xl tablet:text-5xl leading-[1.05] tracking-[-0.02em] mb-10">
            common questions.
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={f.q}
                  className="bg-card/60 border border-border rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-funnel text-primary text-lg">
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-primary/60 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div className="px-6 pb-5 text-foreground/70 leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="py-24 px-6 bg-card/40 scroll-mt-24">
        <div className="max-w-[720px] mx-auto">
          <div className="text-center mb-10">
            <p className="font-cormorant italic text-primary/60 text-2xl mb-2">
              ready when you are
            </p>
            <h2 className="font-funnel font-semibold text-primary text-4xl tablet:text-5xl leading-[1.05] tracking-[-0.02em] mb-3">
              apply to host a pod.
            </h2>
            <p className="text-foreground/70">
              takes 2 minutes. we'll get back within 1-2 working days.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-background border border-border rounded-2xl p-6 tablet:p-8 space-y-4"
          >
            <div className="grid tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                  business name
                </label>
                <input
                  type="text"
                  value={form.business}
                  onChange={(e) => setForm({ ...form, business: e.target.value })}
                  disabled={loading}
                  className={inputClass}
                  placeholder="flat white coffee co."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                  your name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={loading}
                  className={inputClass}
                  placeholder="jane doe"
                />
              </div>
            </div>

            <div className="grid tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                  email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={loading}
                  className={inputClass}
                  placeholder="you@business.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                  phone (optional)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  disabled={loading}
                  className={inputClass}
                  placeholder="+44 7..."
                />
              </div>
            </div>

            <div className="grid tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                  venue type
                </label>
                <select
                  value={form.venueType}
                  onChange={(e) => setForm({ ...form, venueType: e.target.value })}
                  disabled={loading}
                  className={inputClass}
                >
                  {venueOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                  city
                </label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  disabled={loading}
                  className={inputClass}
                  placeholder="london"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                tell us about your space (optional)
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                disabled={loading}
                rows={4}
                className={`${inputClass} resize-none`}
                placeholder="size of space, footfall, anything we should know..."
              />
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading || sent}
              className="w-full rounded-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  sending...
                </>
              ) : sent ? (
                <>
                  <Check className="w-4 h-4" />
                  application sent
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  send application
                </>
              )}
            </Button>

            <p className="text-xs text-center text-foreground/50 pt-2">
              or email us directly at{" "}
              <a
                href="mailto:hello@qwip.co.uk"
                className="underline hover:text-primary"
              >
                hello@qwip.co.uk
              </a>
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PodPartner;
